import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import styled from 'styled-components';
import { Checkbox, Dropdown, Button, Form } from 'semantic-ui-react';
import { AgGridReact } from 'ag-grid-react';
import { showModal, createLoadingSelector } from 'erisxkit/client';
import Datetime from 'react-datetime';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useExternalFilter from 'utils/agGrid/hooks/useExternalFilter';
import { useAgGridCommon } from 'common/table/agGrid/AgGridCommon';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import {
  fetchTaskInfo,
  fetchTasksPromise,
  getTaskNames,
} from 'reducers/taskLogReducer';
import {
  commonPaginationOptions,
  externalFilterOnlyOpts,
  mergeGridOpts,
} from 'utils/agGrid/options';
import { getColDefs, TaskLogGridRow } from './TaskLogGridMetadata';
import { FetchTasksResponse } from 'ts/api/tasks';
import { useAppSelector } from 'hooks/useAppSelector';
import { TASK_LOG } from 'constants/modalTypes';
import { RootState } from 'ts/types/store';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import { ServerSideQuery } from 'utils/agGrid/types';
import { cleanShowFrequentTasksFilters } from './TaskLogGridHelper';
import { processSSRM } from 'utils/agGrid/ssrm/processSSRM';
import Task from 'ts/models/Task.model';

const STATUS_OPTIONS = [
  { key: '1', text: 'Success', value: 'success' },
  { key: '2', text: 'Failed', value: 'fail' },
];

const FieldNameContainer = styled.div`
  min-width: 400px;
  display: flex;
  flex-direction: column;
  margin: 0 10px 0 10px;
  label {
    display: block;
    margin: 0 0 0.28571429rem 0;
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.92857143em;
    font-weight: 700;
    text-transform: none;
  }
`;

const gridOpts = mergeGridOpts(commonPaginationOptions, externalFilterOnlyOpts);

export const TASK_LOG_GRID_NAME = 'task-log';

const TaskLogGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const taskNames: string[] = useAppSelector(getTaskNames);
  const taskLogLoading = useAppSelector((state: RootState) =>
    createLoadingSelector(['TASK_LOG'])(state),
  );
  const tasks = useMemo(
    () =>
      taskNames &&
      taskNames?.map((name) => ({ key: name, value: name, text: name })),
    [taskNames],
  );
  const gridRef = useRef<AgGridReact>(null);

  const colDefs = useMemo(() => getColDefs(taskNames), [taskNames]);

  const { gridVisible, initialGridSettings, onStateUpdated } = useAgGridCommon({
    gridName: TASK_LOG_GRID_NAME,
  });

  const [externalFilters, setExternalFilters] = useState({});
  const context: Record<string, string | string[]> = useMemo(
    () => externalFilters,
    [externalFilters],
  );

  const fetchData = useCallback(
    async (params: IServerSideGetRowsParams) => {
      const { request, context } = params;
      const body = parseAGRequest(request, { transformAttrs: true });
      const bodyFiltered = cleanShowFrequentTasksFilters(body);
      // Base filters from the grid
      const filters = bodyFiltered?.filter || [];
      if (context?.name && context.name.length) {
        filters.push({
          attr: 'name',
          op: 'contain',
          value: context?.name,
        });
      }
      if (context?.dateFrom) {
        filters.push({
          attr: 'time',
          op: 'gte',
          value: context?.dateFrom,
        });
      }
      if (context?.dateTo) {
        filters.push({
          attr: 'time',
          op: 'lte',
          value: context?.dateTo,
        });
      }
      const reqBody: ServerSideQuery = {
        ...body,
        filter: filters,
      };
      const response: FetchTasksResponse = await fetchTasksPromise(
        reqBody,
        dispatch,
      );
      return {
        rows: response?.tasks,
        count: response.count,
      };
    },
    [dispatch],
  );

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => processSSRM<Task>(params, fetchData),
    [fetchData],
  );

  const onGridReadyCallback = useCallback(
    (params: GridReadyEvent) => {
      const dataSource: IServerSideDatasource = {
        getRows,
      };
      params.api?.setGridOption('serverSideDatasource', dataSource);
    },
    [getRows],
  );

  const onFilterChange = useCallback(
    (value: Record<string, string>) => {
      setExternalFilters({ ...externalFilters, ...value });
    },
    [externalFilters],
  );

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshServerSide({ purge: true });
    }
  }, [externalFilters]);

  const {
    setExternalFilter,
    setExternalBooleanFilter,
    getFilterValue,
    clearFilters,
    hasFiltersApplied,
    getBooleanFilterValue,
    onGridReady,
  } = useExternalFilter({ gridRef, onGridReadyCallback });

  const onTaskSelected = useCallback(
    (task: TaskLogGridRow) => {
      dispatch(
        showModal(TASK_LOG, {
          selectedTaskId: task.taskId,
          name: task.name,
          closeOnEscape: true,
          loading: taskLogLoading,
        }),
      );
    },
    [dispatch, taskLogLoading],
  );
  const clearDateFilters = useCallback(() => {
    setExternalFilters({});
  }, []);

  const onClear = useCallback(() => {
    clearFilters();
    clearDateFilters();
  }, [clearDateFilters, clearFilters]);

  const clearDisabled = useMemo(() => {
    const hasExternalFilters = !isEmpty(externalFilters);
    return !hasFiltersApplied && !hasExternalFilters;
  }, [externalFilters, hasFiltersApplied]);

  useEffect(() => {
    dispatch(fetchTaskInfo());
  }, [dispatch]);

  return (
    <div className="ag-theme-quartz">
      {gridVisible && (
        <>
          <Form>
            <Form.Group style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Form.Field
                control={Datetime}
                className="ui input datetime"
                label="Begin Date/Time"
                name="beginTime"
                id="beginTime"
                defaultValue=""
                dateFormat="YYYY-MM-DD"
                timeFormat
                value={context?.dateFrom}
                inputProps={{ placeholder: 'Filter Begin Time' }}
                onChange={(time) => {
                  if (!time || time === '') {
                    onFilterChange({ dateFrom: '' });
                  } else if (
                    typeof time.format === 'function' &&
                    time.isValid()
                  ) {
                    onFilterChange({ dateFrom: time.format() });
                  }
                }}
                closeOnSelect
              />
              <Form.Field
                control={Datetime}
                className="ui input datetime"
                label="End Date/Time"
                name="endTime"
                id="endTime"
                defaultValue=""
                dateFormat="YYYY-MM-DD"
                timeFormat
                value={context?.dateTo}
                inputProps={{ placeholder: 'Filter End Time' }}
                onChange={(time) => {
                  if (!time || time === '') {
                    onFilterChange({ dateTo: '' });
                  } else if (
                    typeof time.format === 'function' &&
                    time.isValid()
                  ) {
                    onFilterChange({ dateTo: time.format() });
                  }
                }}
                closeOnSelect
              />
              <FieldNameContainer>
                <label>Task Name</label>
                <Dropdown
                  value={context?.name || []}
                  placeholder="Click to select Name"
                  onChange={(e, { value }) => {
                    if (value) {
                      onFilterChange({ name: value });
                    }
                  }}
                  name="name"
                  selection
                  multiple
                  search
                  labeled
                  options={tasks}
                />
              </FieldNameContainer>
              <div className="field">
                <label>Status</label>
                <Dropdown
                  value={getFilterValue('type')}
                  placeholder="Click to select Status"
                  onChange={(e, { name: colId, value }) => {
                    if (value)
                      setExternalFilter({ colId, value, op: 'equals' });
                  }}
                  name="type"
                  clearable
                  selection
                  labeled
                  options={STATUS_OPTIONS}
                />
              </div>
              <div className="field">
                <label>Show Frequent Tasks</label>
                <Checkbox
                  name="showFrequentTasks"
                  checked={getBooleanFilterValue('showFrequentTasks')}
                  onChange={(e, { name: colId, checked: value }) => {
                    if (colId !== undefined && gridRef.current) {
                      if (isBoolean(value)) {
                        setExternalBooleanFilter({ colId, value });
                      } else {
                        // This is only necessary because this specific endpoint requires
                        // not to send "false" for showFrequentTask if not set. Hence we remove it
                        gridRef.current.api.destroyFilter(colId);
                      }
                    }
                  }}
                />
              </div>
              <Button
                color="red"
                disabled={clearDisabled}
                id="clear-filters"
                content="Clear"
                onClick={onClear}
                style={{ height: '38px', marginTop: '24px' }}
              />
            </Form.Group>
          </Form>
          <AgGridReact<TaskLogGridRow>
            ref={gridRef}
            columnDefs={colDefs}
            gridOptions={gridOpts}
            onStateUpdated={onStateUpdated}
            initialState={initialGridSettings}
            rowModelType="serverSide"
            onGridReady={onGridReady}
            context={context}
            onRowClicked={({ data }) => {
              if (data) {
                onTaskSelected(data);
              }
            }}
          />
        </>
      )}
    </div>
  );
};

export default TaskLogGrid;
